import type { ImportCounts, ImportPhase, ImportPlan, ImportState } from '@ni/shared';
import { importIsWorking, importPausedAtCeiling, importWaitingForModel } from '../../lib/copy';

/**
 * **The preview bar: whether the dry run is owed, and how loudly** (import note
 * §1.7, §1.11, §2.4).
 *
 * Running the preview again is the gesture the whole session turns on: it is
 * what unblocks Commit, and it is what turns an answered duplicate group back
 * into ordinary rows. It lived inside the People tab, which meant the office
 * could edit the plan, answer six namesakes, walk to the Import tab and find a
 * dimmed button with a sentence under it naming a control on another tab. So
 * the bar is on **every** tab, in the slot the run strip uses, and this is the
 * rule that decides when.
 *
 * A module of its own, and pure, because the suite has no DOM and because the
 * rule is the part worth asserting: four states over five fields, each of which
 * has been the wrong answer at some point.
 */

/** As much of a session as the rule reads. */
export interface PreviewBarSession {
  state: ImportState;
  phase: ImportPhase | null;
  committed: boolean;
  previewed_at: string | null;
  preview_stale: boolean;
  plan: ImportPlan | null;
}

/**
 * - `never` — there is a plan and no dry run has ever been read against it.
 * - `stale` — there was one and something has moved since (a plan edit, a level,
 *   a name fixed inline, a namesake picked).
 * - `running` — the dry run is in flight from wherever it was pressed.
 * - `null` — nothing is owed, so the slot belongs to the run strip or to nobody.
 */
export type PreviewBarState = 'never' | 'stale' | 'running' | null;

/**
 * **Four guards, and every one of them is a bar that must not be on screen.**
 *
 *  - **committed** — after the commit the preview is history: the rows carry
 *    what actually happened, and a bar asking for a dry run of a file already
 *    written would be offering a button the server refuses (`IMPORT_ALREADY_
 *    COMMITTED`).
 *  - **not `in_progress`** — a canceled or unwound session is refused at the
 *    same door. `state` and `committed` are two different questions and both
 *    have to be asked: a commit that failed part way through is `in_progress`
 *    *and* committed.
 *  - **working** — the strip and the bar share one slot and the strip wins.
 *    They cannot both be true today (a session is committed before it imports or
 *    reads), but the slot is the reason the rule says so out loud rather than
 *    leaving it to arithmetic somewhere else. The opening turn is the third kind
 *    of work and needs no clause: a session still being asked its first question
 *    has no plan, so nothing below is owed either.
 *  - **no plan** — for `never` only. A session sitting at `profiled` has never
 *    been previewed and never could be; the bar would be telling the office to
 *    press a button that does not exist yet. Staleness carries its own proof of
 *    a plan, so it is not gated on one.
 *
 * `pending` wins over which of the two it is, because while the dry run is in
 * flight the answer to "why" has stopped being the useful sentence.
 */
export function previewBarState(session: PreviewBarSession, pending: boolean): PreviewBarState {
  if (session.committed || session.state !== 'in_progress') return null;
  if (importIsWorking(session)) return null;

  const owed = session.preview_stale || (session.previewed_at === null && session.plan !== null);
  if (!owed) return null;
  if (pending) return 'running';
  return session.previewed_at === null ? 'never' : 'stale';
}

/* ————————————————————————— the slot itself ————————————————————————— */

/** As much of a session as the slot's rule reads, past the bar's own fields. */
export interface SessionBannerSession extends PreviewBarSession {
  counts: ImportCounts;
  waiting_for_model_until: string | null;
}

/**
 * What is in the full-width slot under the head:
 *
 * - `working` — the tinted strip, pulsing, with the fraction on it.
 * - `paused` — the reading stopped at the spending limit and the office is what
 *   it is waiting for, with the field that raises it.
 * - `waiting` — the reading stopped on the model's rate limit and a **clock** is
 *   what it is waiting for, so the line says when and offers nothing to press.
 * - `preview-*` — the dry run is owed; {@link PreviewBarState} under another
 *   name, so one slot has one vocabulary.
 * - `null` — the slot belongs to nobody and the tabs come straight under the
 *   head.
 */
export type SessionBannerState =
  | 'working'
  | 'paused'
  | 'waiting'
  | 'preview-never'
  | 'preview-stale'
  | 'preview-running'
  | 'question'
  | 'needs-look'
  | 'ready'
  | null;

const PREVIEW_BANNER = {
  never: 'preview-never',
  stale: 'preview-stale',
  running: 'preview-running',
} as const satisfies Record<Exclude<PreviewBarState, null>, SessionBannerState>;

/**
 * **One slot, one rule** (import note §1.11, §2.4) — which of the three things
 * that can be under the head is.
 *
 * **The pause is asked first, and that is the whole of the fix.** A session
 * stopped at its spending limit keeps `phase` at `reading_notes` — the server
 * says so out loud (`pausedForSpend` reads that phase as one of its three
 * conditions, and the leg leaves `error` null and the state `in_progress`
 * precisely so the office can carry on) — so `importIsWorking` is *true* of
 * every paused session. The two are not alternatives to be ordered by which is
 * louder: asking "is it working" first is asking a question whose answer is
 * always yes, and the paused line would never once have appeared. What the
 * office actually saw is the other half of the same fact: a strip pulsing
 * "Reading notes · 86 of 225 · $8.29 so far" over a session that had stopped
 * reading, on every tab, indefinitely.
 *
 * **The model's rate limit is asked next, and for the same reason.** A leg
 * stopped on a quota leaves `reading_notes` on the row exactly as the ceiling
 * does — nothing failed, the units are still pending, the successor is a
 * scheduled task — so `importIsWorking` is true of it as well and the strip
 * would pulse a fraction that has stopped for up to a quarter of an hour. It
 * sits *after* the pause because the two can both be true of one session and
 * only one of them is a thing anybody can act on: a session at its ceiling is
 * waiting on a person whatever the model is doing, and offering the wait instead
 * of the field would take the door away.
 *
 * So: paused beats waiting, waiting beats working, working beats the preview
 * bar, and the preview bar's own rule ({@link previewBarState}) is left to say
 * the rest.
 *
 * **Both of the first two are gated on `in_progress`, for one reason said twice.**
 * The pause offers a door the server refuses on anything else — a canceled
 * session is not waiting on a person, whatever its last leg was doing when it
 * stopped. The strip is the same fact in the other direction: `phase` is the
 * last leg a session ran and not a claim that it is running, so cancelling or
 * unwinding one mid-read leaves `reading_notes` sitting on it, and a pulsing
 * "Reading notes · 86 of 225" over a session that was undone says work is in
 * flight that nothing will ever finish. The state is asked first and the phase
 * only after it: a canceled, undone or complete session gets no strip, whatever
 * its phase says.
 *
 * **And a session before its Import press always has a next step in the slot**
 * (import UX audit, 2026-10-24). Once the preview ran, the slot went empty and
 * the only sign that Import was next was a grey clause in the head — an operator
 * left two uploads unimported for days that way. So after the preview bar come
 * the three quiet states: the planner's open question (only where no preview is
 * owed, since the bar is the one place to run it), rows that still need a look,
 * and ready. None of them presses anything; they point at the tab that does.
 *
 * Pure, and tested as a table, because every one of these answers has been the
 * wrong thing on that slot at some point.
 */
export function sessionBannerState(
  session: SessionBannerSession,
  previewPending: boolean,
): SessionBannerState {
  const live = session.state === 'in_progress';
  if (live && importPausedAtCeiling(session)) return 'paused';
  if (live && importWaitingForModel(session)) return 'waiting';
  if (live && importIsWorking(session)) return 'working';

  const preview = previewBarState(session, previewPending);
  if (preview !== null) return PREVIEW_BANNER[preview];

  if (!live || session.committed) return null;
  if (session.phase === 'waiting_on_you') return 'question';
  if (session.previewed_at === null) return null;
  return session.counts.needs_a_look > 0 ? 'needs-look' : 'ready';
}
